/**
 * API Service - Production Ready
 * Centralized API communication with proper error handling
 */

// Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";
const USE_NEW_UI_RESOLUTION_PIPELINE =
  process.env.REACT_APP_USE_NEW_UI_RESOLUTION_PIPELINE !== "false";
const USE_NEW_UI_DOCUMENT_PIPELINE =
  process.env.REACT_APP_USE_NEW_UI_DOCUMENT_PIPELINE !== "false";
const TOKEN_KEY = "cs_token";
const REFRESH_TOKEN_KEY = "cs_refresh";
const USER_KEY = "cs_user";

// ==================== Token Management ====================
// Supports "Remember me": localStorage (persistent) or sessionStorage (session-only)
const STORAGE_MODE_KEY = "cs_storage_mode"; // stored in localStorage always

const tokenManager = {
  // Determine which storage to use
  _getStorage: () => {
    try {
      return localStorage.getItem(STORAGE_MODE_KEY) === "persistent"
        ? localStorage
        : sessionStorage;
    } catch {
      return sessionStorage;
    }
  },

  /**
   * Set storage mode: "persistent" (localStorage) or "session" (sessionStorage)
   * Called before storing tokens during login.
   */
  setStorageMode: (persistent) => {
    localStorage.setItem(
      STORAGE_MODE_KEY,
      persistent ? "persistent" : "session",
    );
  },

  getToken: () => {
    // Check both storages (handles mode switch + existing sessions)
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  },
  setToken: (token) => tokenManager._getStorage().setItem(TOKEN_KEY, token),
  removeToken: () => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  },

  getRefreshToken: () => {
    return (
      localStorage.getItem(REFRESH_TOKEN_KEY) ||
      sessionStorage.getItem(REFRESH_TOKEN_KEY)
    );
  },
  setRefreshToken: (token) =>
    tokenManager._getStorage().setItem(REFRESH_TOKEN_KEY, token),
  removeRefreshToken: () => {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  // User data (non-sensitive)
  getUser: () => {
    try {
      const data =
        localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  },
  setUser: (user) => {
    // Only store non-sensitive user info
    const safeUser = {
      id: user.id || user._id,
      fullName: user.fullName,
      email: user.email,
      mobile: user.mobile,
      roleName: user.roleName,
      companyName: user.companyName,
      designation: user.designation,
      profession: user.profession,
      membershipNo: user.membershipNo,
      cin: user.cin,
      state: user.state,
      isPhoneVerified: user.isPhoneVerified,
      isEmailVerified: user.isEmailVerified,
    };
    tokenManager._getStorage().setItem(USER_KEY, JSON.stringify(safeUser));
  },
  removeUser: () => {
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(USER_KEY);
  },

  clearAll: () => {
    // Clear from both storages
    [localStorage, sessionStorage].forEach((s) => {
      s.removeItem(TOKEN_KEY);
      s.removeItem(REFRESH_TOKEN_KEY);
      s.removeItem(USER_KEY);
    });
    localStorage.removeItem(STORAGE_MODE_KEY);
  },

  isAuthenticated: () =>
    !!(localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY)),
};

// ==================== API Client ====================

class APIError extends Error {
  constructor(message, code, status, details = null) {
    super(message);
    this.name = "APIError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const getHeaders = (includeAuth = true) => {
  const headers = { "Content-Type": "application/json" };

  if (includeAuth) {
    const token = tokenManager.getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

// Helper function to trigger logout with error message
const triggerLogout = (errorMessage, errorCode) => {
  const logoutMessage =
    errorMessage || "Your session has expired. Please login again.";

  // Clear auth first
  tokenManager.clearAll();

  // Dispatch logout event with error details immediately
  // AuthContext will handle showing toast and redirecting
  const event = new CustomEvent("auth:logout", {
    detail: {
      message: logoutMessage,
      code: errorCode || "SESSION_EXPIRED",
    },
    bubbles: true,
    cancelable: false,
  });

  // Dispatch synchronously to ensure it's handled
  window.dispatchEvent(event);
};

const handleResponse = async (response) => {
  let data = {};
  const statusCode = response.status;
  // Whether we actually got a JSON body to reason about. A 403 we cannot read is
  // treated differently from one we can — see `shouldLogout` below.
  let bodyParsed = false;

  try {
    const text = await response.text();
    if (text) {
      data = JSON.parse(text);
      bodyParsed = true;
    }
  } catch (e) {
    // If response is not JSON, create a basic error object
    data = { message: response.statusText || "Request failed" };
  }

  // Check for errors in response (even if response.ok is true)
  // Some APIs return success: false in the body even with 200 status
  if (!response.ok || data.success === false) {
    // Error codes that should trigger logout
    // Add more codes here as needed, or set shouldLogout: true in API response
    const logoutErrorCodes = [
      "SESSION_EXPIRED",
      "TOKEN_EXPIRED",
      "INVALID_TOKEN",
      "UNAUTHORIZED",
      "ACCOUNT_SUSPENDED",
      "ACCOUNT_DISABLED",
      "ACCOUNT_DELETED",
      "FORBIDDEN_ACCESS",
      "UNAUTHORIZED_ACCESS",
      "RATE_LIMIT_EXCEEDED",
    ];

    const errorCode =
      data.code ||
      (statusCode === 401
        ? "UNAUTHORIZED"
        : statusCode === 403
          ? "FORBIDDEN"
          : "ERROR");
    const errorMessage =
      data.message ||
      (statusCode === 401
        ? "Unauthorized. Please login again."
        : statusCode === 403
          ? "Access forbidden."
          : "Request failed");

    // Check if this error should trigger logout
    // You can trigger logout by:
    // 1. Including error code in logoutErrorCodes array above
    // 2. Returning HTTP status 401
    // 3. Setting shouldLogout: true in API response
    //
    // 403 is deliberately NOT a blanket logout trigger. It is two different
    // things wearing one status code: "your token is no longer good" (a session
    // failure) and "you do not have permission to do this" (an ordinary,
    // recoverable error). Treating both as the first meant a read-only
    // collaborator who tapped a write button — `code:
    // 'INSUFFICIENT_COMPANY_ACCESS'` from resolveForWriteOrRespond — got thrown
    // out of the whole app instead of seeing "you have view-only access".
    //
    // Genuine token failures still end the session: they arrive either as a 401
    // or as a 403 carrying one of the token/account codes in `logoutErrorCodes`
    // (SESSION_EXPIRED, INVALID_TOKEN, ACCOUNT_SUSPENDED, FORBIDDEN_ACCESS, …).
    //
    // Conservative fallback: a 403 whose body we could not parse tells us
    // nothing, so it keeps the old logout behaviour rather than silently
    // stranding someone on a dead session.
    const shouldLogout =
      logoutErrorCodes.includes(errorCode) ||
      statusCode === 401 ||
      data.shouldLogout === true ||
      (statusCode === 403 && !bodyParsed);

    if (shouldLogout) {
      triggerLogout(errorMessage, errorCode);
    }

    const apiError = new APIError(
      errorMessage,
      errorCode,
      statusCode,
      data.details || data.errors,
    );
    // Records that the logout question was already ASKED AND ANSWERED for this
    // response, so apiRequest's catch block does not re-decide it from the bare
    // status code and undo the distinction drawn above.
    apiError.logoutHandled = true;
    throw apiError;
  }

  return data;
};

const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    method: options.method || "GET",
    headers: getHeaders(options.includeAuth !== false),
    credentials: "include", // send the HttpOnly refresh cookie (path-scoped to /api/auth)
    ...options,
  };

  if (
    options.body &&
    typeof options.body === "object" &&
    !(options.body instanceof FormData)
  ) {
    config.body = JSON.stringify(options.body);
  } else if (options.body) {
    config.body = options.body;
  }

  try {
    const response = await fetch(url, config);

    // Handle 401 - try token refresh
    if (
      response.status === 401 &&
      tokenManager.getToken() &&
      !options.skipRefresh
    ) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        config.headers.Authorization = `Bearer ${tokenManager.getToken()}`;
        const retryResponse = await fetch(url, config);
        return handleResponse(retryResponse);
      }
      // Refresh failed - let handleResponse handle the logout with proper error message
      // Continue to handleResponse to parse the error and trigger logout
      // Don't return early - let it go through handleResponse
    }

    // Always go through handleResponse to check for logout conditions
    return handleResponse(response);
  } catch (error) {
    // If it's an APIError that should trigger logout, it's already handled
    if (error instanceof APIError) {
      // `logoutHandled` means handleResponse already weighed this exact response
      // (including the permission-403 carve-out) and acted or deliberately did
      // not. Only errors that never passed through it — an APIError thrown from
      // somewhere else in the request path — fall back to the status-code rule.
      if (!error.logoutHandled && (error.status === 401 || error.status === 403)) {
        // Logout might not have been triggered, trigger it now
        triggerLogout(error.message, error.code);
      }
      throw error;
    }
    throw new APIError(
      error.message || "Network error. Please check your connection.",
      "NETWORK_ERROR",
      0,
    );
  }
};

// Token refresh helper — the refresh token is an HttpOnly cookie, so there is no
// body; the browser sends the cookie automatically with credentials: 'include'.
let refreshInFlight = null;
const refreshAccessToken = async () => {
  // Single-flight: concurrent 401s share one refresh call so we don't trigger
  // two rotations (the second would fail against the just-rotated token).
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok && data.success) {
        tokenManager.setToken(data.data.accessToken);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
};

// ==================== Auth API ====================

export const authAPI = {
  /**
   * Register new user
   */
  register: async (userData) => {
    const response = await apiRequest("/auth/register", {
      method: "POST",
      body: userData,
      includeAuth: false,
    });
    return response;
  },

  /**
   * Login - sends OTP to mobile
   */
  login: async (mobile) => {
    const response = await apiRequest("/auth/login", {
      method: "POST",
      body: { mobile },
      includeAuth: false,
    });
    return response;
  },

  /**
   * Login with email/mobile + password (no OTP required)
   * @param {string} identifier - Email address or 10-digit mobile number
   * @param {string} password - Account password
   * @param {boolean} rememberMe - If true, use persistent storage (localStorage)
   */
  passwordLogin: async (identifier, password, rememberMe = false) => {
    // Set storage mode BEFORE storing tokens
    tokenManager.setStorageMode(rememberMe);

    const response = await apiRequest("/auth/login/password", {
      method: "POST",
      body: { identifier, password },
      includeAuth: false,
    });

    if (response.success && response.data) {
      if (response.data.tokens) {
        tokenManager.setToken(response.data.tokens.accessToken);
        // refresh token now arrives as an HttpOnly cookie — nothing to store here
      }
      if (response.data.user) {
        tokenManager.setUser(response.data.user);
      }
    }

    return response;
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email) => {
    return apiRequest("/auth/forgot-password", {
      method: "POST",
      body: { email },
      includeAuth: false,
    });
  },

  /**
   * Reset password with token from email link
   */
  resetPassword: async (token, password) => {
    return apiRequest("/auth/reset-password", {
      method: "POST",
      body: { token, password },
      includeAuth: false,
    });
  },

  /**
   * Verify OTP (unified for both login and registration)
   */
  verifyOTP: async (mobile, otp) => {
    const response = await apiRequest("/auth/verify-otp", {
      method: "POST",
      body: { mobile, otp },
      includeAuth: false,
    });

    if (response.success && response.data) {
      // Store tokens and user
      if (response.data.tokens) {
        tokenManager.setToken(response.data.tokens.accessToken);
        // refresh token now arrives as an HttpOnly cookie — nothing to store here
      }
      if (response.data.user) {
        tokenManager.setUser(response.data.user);
      }
    }

    return response;
  },

  /**
   * Resend OTP
   */
  resendOTP: async (mobile) => {
    return apiRequest("/auth/resend-otp", {
      method: "POST",
      body: { mobile },
      includeAuth: false,
    });
  },

  /**
   * Verify email OTP (step 2 of 2FA after passwordLogin). Returns tokens on success.
   */
  verifyEmailOtp: async (identifier, otp) => {
    const response = await apiRequest("/auth/verify-email-otp", {
      method: "POST",
      body: { identifier, otp },
      includeAuth: false,
    });

    if (response.success && response.data) {
      if (response.data.tokens) {
        tokenManager.setToken(response.data.tokens.accessToken);
        // refresh token now arrives as an HttpOnly cookie — nothing to store here
      }
      if (response.data.user) {
        tokenManager.setUser(response.data.user);
      }
    }

    return response;
  },

  /** Resend the in-progress email OTP. Generic response; backend enforces 60s cooldown. */
  resendEmailOtp: async (identifier) => {
    return apiRequest("/auth/resend-email-otp", {
      method: "POST",
      body: { identifier },
      includeAuth: false,
    });
  },

  /** Fetch a short-lived single-use ticket for the WebSocket handshake. */
  getWsTicket: () => apiRequest("/auth/ws-ticket", { method: "POST" }),

  /** Consume a magic-link token (POST, called by /magic-login interstitial). */
  magicLogin: async (token, rememberMe = false) => {
    // Set storage mode before storing tokens (mirrors passwordLogin).
    tokenManager.setStorageMode(rememberMe);

    const response = await apiRequest("/auth/magic-login", {
      method: "POST",
      body: { token },
      includeAuth: false,
    });

    if (response.success && response.data) {
      if (response.data.tokens) {
        tokenManager.setToken(response.data.tokens.accessToken);
        // refresh token now arrives as an HttpOnly cookie — nothing to store here
      }
      if (response.data.user) {
        tokenManager.setUser(response.data.user);
      }
    }

    return response;
  },

  /**
   * Get current user profile
   */
  getProfile: async () => {
    const response = await apiRequest("/auth/profile");
    if (response.success && response.data?.user) {
      tokenManager.setUser(response.data.user);
    }
    return response;
  },

  /**
   * Update profile
   */
  updateProfile: async (data) => {
    const response = await apiRequest("/auth/profile", {
      method: "PUT",
      body: data,
    });
    if (response.success && response.data?.user) {
      tokenManager.setUser(response.data.user);
    }
    return response;
  },

  /**
   * Logout
   */
  logout: async () => {
    try {
      await apiRequest("/auth/logout", { method: "POST" });
    } catch {
      // Ignore errors
    } finally {
      tokenManager.clearAll();
    }
  },

  /**
   * Logout from all devices
   */
  logoutAll: async () => {
    try {
      await apiRequest("/auth/logout-all", { method: "POST" });
    } catch {
      // Ignore errors
    } finally {
      tokenManager.clearAll();
    }
  },

  /**
   * Change password. Body matches the validatePasswordChange validator:
   * currentPassword, newPassword, confirmPassword.
   */
  changePassword: async (currentPassword, newPassword) => {
    return apiRequest('/auth/change-password', {
      method: 'PUT',
      body: { currentPassword, newPassword, confirmPassword: newPassword },
    });
  },

  /** List the current user's active device sessions. */
  getSessions: async () => {
    return apiRequest('/auth/sessions');
  },

  /** Revoke one session by id (the current session can't be revoked here). */
  revokeSession: async (id) => {
    return apiRequest(`/auth/sessions/${id}`, { method: 'DELETE' });
  },

  /** Effective permissions for the current user (admin profile). */
  getPermissions: async () => {
    return apiRequest('/auth/permissions');
  },

  /** Self-scoped activity trail — recent actions this account performed. */
  getMyActivity: async ({ cursor, limit } = {}) => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit) params.set('limit', limit);
    const query = params.toString();
    return apiRequest(`/auth/my-activity${query ? `?${query}` : ''}`);
  },

  /**
   * Download the current user's data export as a JSON file (DPDP portability).
   * Mirrors the authenticated blob-download pattern used by servicesAPI.
   */
  exportData: async () => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/auth/data-export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || 'Data export failed',
        error.code || 'EXPORT_ERROR',
        response.status
      );
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `complyv-data-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { success: true };
  },
};

// ==================== Services API ====================

export const servicesAPI = {
  // Public endpoints
  getAllServices: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/services${query ? `?${query}` : ""}`, {
      includeAuth: false,
    });
  },

  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/services${query ? `?${query}` : ""}`, {
      includeAuth: false,
    });
  },

  getBySlug: async (slug) => {
    return apiRequest(`/services/${slug}`, { includeAuth: false });
  },

  getServiceBySlug: async (slug) => {
    return apiRequest(`/services/${slug}`, { includeAuth: false });
  },

  getCategories: async () => {
    return apiRequest("/services/categories", { includeAuth: false });
  },

  getServiceCategories: async () => {
    return apiRequest("/services/categories", { includeAuth: false });
  },

  // Download raw/empty template for a service
  downloadRawTemplate: async (slug) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/services/${slug}/template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || "Failed to download template",
        error.code || "DOWNLOAD_ERROR",
        response.status,
      );
    }
    return response.blob();
  },

  // Admin
  createService: async (data) =>
    apiRequest("/services", { method: "POST", body: data }),
  create: async (data) =>
    apiRequest("/services", { method: "POST", body: data }),
  updateService: async (id, data) =>
    apiRequest(`/services/${id}`, { method: "PUT", body: data }),
  update: async (id, data) =>
    apiRequest(`/services/${id}`, { method: "PUT", body: data }),
  deleteService: async (id) =>
    apiRequest(`/services/${id}`, { method: "DELETE" }),
  delete: async (id) => apiRequest(`/services/${id}`, { method: "DELETE" }),
};

// ==================== Cart API ====================

export const cartAPI = {
  getCart: async () => apiRequest("/cart"),

  addToCart: async (serviceId, quantity = 1) => {
    return apiRequest("/cart/add", {
      method: "POST",
      body: { serviceId, quantity },
    });
  },

  updateCartItem: async (serviceId, quantity) => {
    return apiRequest(`/cart/item/${serviceId}`, {
      method: "PUT",
      body: { quantity },
    });
  },

  removeFromCart: async (serviceId) => {
    return apiRequest(`/cart/item/${serviceId}`, { method: "DELETE" });
  },

  clearCart: async () => {
    return apiRequest("/cart/clear", { method: "DELETE" });
  },
};

// ==================== Orders API ====================

export const ordersAPI = {
  create: async (paymentMethod) => {
    return apiRequest("/orders", {
      method: "POST",
      body: { paymentMethod },
    });
  },

  createOrder: async (paymentMethod) => {
    return apiRequest("/orders", {
      method: "POST",
      body: { paymentMethod },
    });
  },

  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/orders${query ? `?${query}` : ""}`);
  },

  getUserOrders: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/orders${query ? `?${query}` : ""}`);
  },

  getById: async (id) => apiRequest(`/orders/${id}`),
  getOrderById: async (id) => apiRequest(`/orders/${id}`),

  cancel: async (id) => {
    return apiRequest(`/orders/${id}/cancel`, { method: "PUT" });
  },

  cancelOrder: async (id) => {
    return apiRequest(`/orders/${id}/cancel`, { method: "PUT" });
  },

  // Admin
  getAllAdmin: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/orders/admin/all${query ? `?${query}` : ""}`);
  },

  updateStatus: async (id, statusData) => {
    return apiRequest(`/orders/${id}/status`, {
      method: "PUT",
      body: statusData,
    });
  },
};

// ==================== Documents API ====================

export const documentsAPI = {
  upload: async (formData) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    return response.json();
  },

  uploadDocument: async (formData) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    return response.json();
  },

  getForOrder: async (orderId) => {
    return apiRequest(`/documents/order/${orderId}`);
  },

  getOrderDocuments: async (orderId) => {
    return apiRequest(`/documents/order/${orderId}`);
  },

  getDocumentStatus: async (orderId) => {
    return apiRequest(`/documents/order/${orderId}/status`);
  },

  submitOrder: async (orderId) => {
    return apiRequest(`/documents/order/${orderId}/submit`, { method: "POST" });
  },

  checkOrderComplete: async (orderId) => {
    return apiRequest(`/documents/check-order-complete/${orderId}`, {
      method: "POST",
    });
  },

  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/documents${query ? `?${query}` : ""}`);
  },

  getUserDocuments: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/documents${query ? `?${query}` : ""}`);
  },

  getById: async (id) => apiRequest(`/documents/${id}`),

  delete: async (id) => {
    return apiRequest(`/documents/${id}`, { method: "DELETE" });
  },

  deleteDocument: async (id) => {
    return apiRequest(`/documents/${id}`, { method: "DELETE" });
  },

  // Admin
  process: async (id, action, data = {}) => {
    return apiRequest(`/documents/${id}/process`, {
      method: "PUT",
      body: { action, ...data },
    });
  },

  download: async (id) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok)
      throw new APIError("Download failed", "DOWNLOAD_ERROR", response.status);
    return response.blob();
  },

  downloadDocument: async (id) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/documents/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok)
      throw new APIError("Download failed", "DOWNLOAD_ERROR", response.status);
    return response.blob();
  },
};

// ==================== Meetings API ====================

export const meetingsAPI = {
  getTemplate: async () => {
    const response = await fetch(`${API_BASE_URL}/meetings/minutes/template`, {
      headers: getHeaders(),
    });
    if (!response.ok)
      throw new APIError(
        "Failed to download template",
        "DOWNLOAD_ERROR",
        response.status,
      );
    return response.blob();
  },

  generateMinutes: async (file, format = "docx") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("format", format);

    const response = await fetch(`${API_BASE_URL}/meetings/minutes/generate`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || "Generation failed",
        error.code || "ERROR",
        response.status,
      );
    }

    return response.blob();
  },

  previewData: async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${API_BASE_URL}/meetings/minutes/preview`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
      body: formData,
    });

    return handleResponse(response);
  },

  // ---- Meetings ------------------------------------------------------------
  // Company-scoped, matching the registers. Everything below drives the
  // recording -> transcript -> minutes pipeline.

  list: async (companyId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
    ).toString();
    return apiRequest(`/meetings/${companyId}${qs ? `?${qs}` : ""}`);
  },

  /** Directors who may be marked as attending — never typed by hand. */
  eligibleDirectors: async (companyId) =>
    apiRequest(`/meetings/${companyId}/directors`),

  /** Whether transcription is configured, and whether it separates speakers. */
  transcriptionStatus: async (companyId) =>
    apiRequest(`/meetings/${companyId}/transcription-status`),

  create: async (companyId, meeting) =>
    apiRequest(`/meetings/${companyId}`, { method: "POST", body: meeting }),

  get: async (companyId, meetingId) =>
    apiRequest(`/meetings/${companyId}/${meetingId}`),

  update: async (companyId, meetingId, changes) =>
    apiRequest(`/meetings/${companyId}/${meetingId}`, {
      method: "PATCH",
      body: changes,
    }),

  remove: async (companyId, meetingId) =>
    apiRequest(`/meetings/${companyId}/${meetingId}`, { method: "DELETE" }),

  /**
   * Send the audio. `source` is 'upload' for a file the user chose and 'in_app'
   * for something recorded in the browser — the backend stores both the same way
   * and only records which it was.
   *
   * Uses fetch directly rather than apiRequest so the browser sets the multipart
   * boundary itself; apiRequest's JSON content-type header would break it.
   */
  /**
   * `consentConfirmed` is required when we did the recording ourselves — the
   * server refuses an in-app recording without it, because in that flow the duty
   * to have told the room sits with us rather than with the client's policy.
   */
  uploadRecording: async (companyId, meetingId, file, source = "upload", consentConfirmed = false) => {
    const formData = new FormData();
    formData.append("recording", file);
    formData.append("source", source);
    if (consentConfirmed) formData.append("consentConfirmed", "true");

    const response = await fetch(
      `${API_BASE_URL}/meetings/${companyId}/${meetingId}/recording`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
        body: formData,
      },
    );

    return handleResponse(response);
  },

  retryTranscription: async (companyId, meetingId) =>
    apiRequest(`/meetings/${companyId}/${meetingId}/recording/retry`, {
      method: "POST",
    }),

  getTranscript: async (companyId, meetingId) =>
    apiRequest(`/meetings/${companyId}/${meetingId}/transcript`),

  /** `speakerMap` is { "Speaker 1": attendeeId }. An empty value clears one. */
  setSpeakers: async (companyId, meetingId, speakerMap) =>
    apiRequest(`/meetings/${companyId}/${meetingId}/speakers`, {
      method: "PUT",
      body: { speakerMap },
    }),

  draftMinutes: async (companyId, meetingId, { withoutTranscript } = {}) =>
    apiRequest(
      `/meetings/${companyId}/${meetingId}/minutes${withoutTranscript ? "?withoutTranscript=true" : ""}`,
      { method: "POST" },
    ),

  approve: async (companyId, meetingId) =>
    apiRequest(`/meetings/${companyId}/${meetingId}/approve`, {
      method: "POST",
    }),
};

// ==================== AI Models API ====================

export const aiModelsAPI = {
  // Get all models (admin only)
  getAll: async () => {
    return apiRequest("/ai-models");
  },

  // Lightweight counts ({ total, active }) for the dashboard KPI
  getStats: async () => {
    return apiRequest("/ai-models/stats");
  },

  // Get model by ID
  getById: async (id) => {
    return apiRequest(`/ai-models/${id}`);
  },

  // Create new model
  create: async (modelData) => {
    return apiRequest("/ai-models", {
      method: "POST",
      body: modelData,
    });
  },

  // Update model
  update: async (id, modelData) => {
    return apiRequest(`/ai-models/${id}`, {
      method: "PUT",
      body: modelData,
    });
  },

  // Delete model
  delete: async (id) => {
    return apiRequest(`/ai-models/${id}`, { method: "DELETE" });
  },

  // Test model
  test: async (id) => {
    return apiRequest(`/ai-models/${id}/test`, { method: "POST" });
  },

  // Set as default
  setDefault: async (id) => {
    return apiRequest(`/ai-models/${id}/set-default`, { method: "POST" });
  },

  // Get provider options
  getProviders: async () => {
    return apiRequest("/ai-models/providers");
  },

  // Get default model
  getDefault: async () => {
    return apiRequest("/ai-models/default");
  },

  // Get usage logs
  getUsageLogs: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/ai-models/usage/logs${query ? `?${query}` : ""}`);
  },

  // Get usage summary
  getUsageSummary: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/ai-models/usage/summary${query ? `?${query}` : ""}`);
  },

  // Get usage for an order
  getOrderUsage: async (orderId) => {
    return apiRequest(`/ai-models/usage/order/${orderId}`);
  },
};

// ==================== Entitlements (admin) ====================
// Persona defaults + per-user overrides for AI features and usage quotas.
export const adminEntitlementsAPI = {
  // Built-in defaults + persona rows + all user overrides (identity attached).
  getAll: async () => apiRequest("/admin/entitlements"),

  // Patch a persona default: { features?, quotas? } — null on a field = inherit.
  updatePersona: async (persona, patch) =>
    apiRequest(`/admin/entitlements/persona/${persona}`, { method: "PUT", body: patch }),

  // Patch a per-user override: { features?, quotas?, expiresAt? }.
  updateUser: async (userId, patch) =>
    apiRequest(`/admin/entitlements/user/${userId}`, { method: "PUT", body: patch }),

  // Remove a per-user override (revert to persona default).
  clearUser: async (userId) =>
    apiRequest(`/admin/entitlements/user/${userId}`, { method: "DELETE" }),
};

// ==================== Compliance checklist (per company) ====================
export const complianceChecklistAPI = {
  // Every obligation the rule book has an opinion about, in three buckets.
  // `asOf` answers for a past period, against the rule versions in force then.
  get: async (companyId, asOf) =>
    apiRequest(`/companies/${companyId}/checklist${asOf ? `?asOf=${asOf}` : ""}`),
};

// ==================== Compliance rule book (admin) ====================
// The rule book is versioned, never overwritten. Note that amend and correct
// are separate calls on purpose: amending preserves what a past checklist said,
// correcting rewrites it, and only one of those is right for a given change.
export const complianceRulesAPI = {
  // filters: { act, jurisdiction, state, periodicity, q, status, page, limit }
  // status: 'current' (default) | 'superseded' | 'all'
  list: async (filters = {}) => {
    const qs = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined)
    ).toString();
    return apiRequest(`/admin/compliance-rules${qs ? `?${qs}` : ""}`);
  },

  acts: async () => apiRequest("/admin/compliance-rules/acts"),

  versions: async (ruleKey) =>
    apiRequest(`/admin/compliance-rules/${encodeURIComponent(ruleKey)}/versions`),

  create: async (rule) => apiRequest("/admin/compliance-rules", { method: "POST", body: rule }),

  // We got it wrong. Edits one version in place; refused on a closed version.
  correct: async (id, patch) =>
    apiRequest(`/admin/compliance-rules/${id}`, { method: "PUT", body: patch }),

  // The law changed. Closes the standing version, opens the next.
  amend: async (ruleKey, changes, effectiveFrom) =>
    apiRequest(`/admin/compliance-rules/${encodeURIComponent(ruleKey)}/amend`, {
      method: "POST",
      body: { ...changes, effectiveFrom },
    }),

  // Go back to an earlier version's wording. Opens a NEW version carrying that
  // content rather than reopening the old one — reopening would rewrite what
  // past checklists said, which is the thing versions exist to prevent.
  revertTo: async (ruleKey, version, effectiveFrom, reason = '') =>
    apiRequest(`/admin/compliance-rules/${encodeURIComponent(ruleKey)}/revert-to/${version}`, {
      method: "POST",
      body: { effectiveFrom, reason },
    }),

  // Repealed. Closes the standing version and opens nothing — never a delete,
  // because the rule still applied to every period before this date.
  retire: async (ruleKey, effectiveTo, reason) =>
    apiRequest(`/admin/compliance-rules/${encodeURIComponent(ruleKey)}/retire`, {
      method: "POST",
      body: { effectiveTo, reason },
    }),

  // Check an expression without saving it.
  validateExpression: async (appliesWhen) =>
    apiRequest("/admin/compliance-rules/validate-expression", {
      method: "POST",
      body: { appliesWhen },
    }),

  // The blank template, with a worked example row and a field reference sheet.
  // Fetched rather than linked: the route is behind adminOnly, and a plain <a>
  // would not carry the token.
  downloadTemplate: async () => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/admin/compliance-rules/template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(error.message || "Could not download the template", "DOWNLOAD_ERROR", response.status);
    }
    return response.blob();
  },

  // Dry run unless apply is true. The dry run reports exactly what would be
  // created and every row that would be rejected, with the reason and the
  // spreadsheet row number — rules decide what a client is told they must file,
  // so nothing is written before someone has looked.
  importWorkbook: async (file, apply = false) => {
    const token = tokenManager.getToken();
    const body = new FormData();
    body.append("file", file);
    body.append("apply", String(apply));
    const response = await fetch(`${API_BASE_URL}/admin/compliance-rules/import`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body,
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || !json.success) {
      throw new APIError(json.message || "Import failed", json.code || "IMPORT_ERROR", response.status);
    }
    return json;
  },
};

// ==================== My AI Usage (self-scoped) ====================
export const myAiUsageAPI = {
  // The signed-in user's own AI usage + daily budget. days: 1–365.
  get: async (days = 30) => apiRequest(`/ai/usage/me?days=${days}`),
};

// ==================== AI Control Plane (admin) ====================
// Live AI runtime config (kill-switch, tiers, limits, routing) via the
// resolver-backed settings endpoints. Mounted at /api/admin/ai.
export const adminAiAPI = {
  // Merged config + which keys are DB-overridden + the editable schema.
  getSettings: async () => {
    return apiRequest("/admin/ai/settings");
  },

  // Apply overrides ({ updates: { "chat.enabled": false } }) and/or clear keys
  // back to the env default ({ clear: ["chat.maxTokens"] }).
  updateSettings: async ({ updates, clear } = {}) => {
    return apiRequest("/admin/ai/settings", {
      method: "PUT",
      body: { ...(updates ? { updates } : {}), ...(clear ? { clear } : {}) },
    });
  },

  // Governance — agent-action policy (enable / role override) + activity feed.
  getAgentPolicies: async () => {
    return apiRequest("/admin/ai/agent-policies");
  },
  updateAgentPolicy: async ({ key, enabled, rolesOverride } = {}) => {
    return apiRequest("/admin/ai/agent-policies", {
      method: "PUT",
      body: { key, ...(enabled !== undefined ? { enabled } : {}), ...(rolesOverride !== undefined ? { rolesOverride } : {}) },
    });
  },
  getAgentActivity: async (limit = 50) => {
    return apiRequest(`/admin/ai/agent-activity?limit=${limit}`);
  },

  // Answer review queue: thumbs-down replies for an expert to judge.
  getAnswerReview: async ({ status = "new", page = 1, limit = 20 } = {}) => {
    return apiRequest(`/admin/ai/answer-review?status=${encodeURIComponent(status)}&page=${page}&limit=${limit}`);
  },
  reviewAnswer: async (id, { status, category, note, correction }) => {
    return apiRequest(`/admin/ai/answer-review/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { status, category, note, correction },
    });
  },
  getAnswerReviewGolden: async () => {
    return apiRequest("/admin/ai/answer-review/golden");
  },

  // Vee traces: how turns ran (timings and counts, never text).
  getTraceSummary: async (hours = 24) => {
    return apiRequest(`/admin/ai/traces/summary?hours=${hours}`);
  },
  getTraces: async ({ only = "", limit = 50 } = {}) => {
    return apiRequest(`/admin/ai/traces?limit=${limit}${only ? `&only=${encodeURIComponent(only)}` : ""}`);
  },

  // Live budget governance: the global token counter vs the resolved caps +
  // threshold-alert state. Historical spend is on Usage & Cost now.
  getBudget: async () => {
    return apiRequest("/admin/ai/budget");
  },

  // Read-only firm-template oversight: firm recipes + smoke-failure counts.
  // Super-admin only (the backend 403s a plain admin; the nav also hides it).
  getFirmTemplates: async () => {
    return apiRequest("/admin/ai/firm-templates");
  },
};

// ==================== Company Profile API ====================

export const companyProfileAPI = {
  // Get user's company profile
  getProfile: async () => {
    return apiRequest("/company-profile");
  },

  // Get service-specific profile
  getServiceProfile: async (serviceType) => {
    return apiRequest(`/company-profile/service/${serviceType}`);
  },

  // Download pre-populated template
  downloadTemplate: async (
    serviceType = "board_meeting",
    meetingNumber = null,
  ) => {
    const token = tokenManager.getToken();
    const params = new URLSearchParams({ serviceType });
    if (meetingNumber) params.append("meetingNumber", meetingNumber);

    const response = await fetch(
      `${API_BASE_URL}/company-profile/template/prepopulated?${params}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || "Failed to download template",
        error.code || "DOWNLOAD_ERROR",
        response.status,
      );
    }

    return response.blob();
  },
};

// ==================== Users API (Admin) ====================

export const usersAPI = {
  // Get all users (admin only)
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/auth/admin/users${query ? `?${query}` : ""}`);
  },

  // Get user by ID
  getById: async (id) => {
    return apiRequest(`/auth/admin/users/${id}`);
  },

  // Update user
  update: async (id, userData) => {
    return apiRequest(`/auth/admin/users/${id}`, {
      method: "PUT",
      body: userData,
    });
  },

  // Delete user
  delete: async (id) => {
    return apiRequest(`/auth/admin/users/${id}`, { method: "DELETE" });
  },

  // Toggle user status
  toggleStatus: async (id) => {
    return apiRequest(`/auth/admin/users/${id}/toggle-status`, {
      method: "PUT",
    });
  },

  // Change user role. `role` may be a role name (e.g. "professional") or a Role ObjectId.
  changeRole: async (id, role) => {
    return apiRequest(`/auth/admin/users/${id}/role`, {
      method: "PUT",
      body: { role },
    });
  },

  // Update user status (activate/deactivate)
  updateStatus: async (id, isActive) => {
    return apiRequest(`/auth/admin/users/${id}/status`, {
      method: "PUT",
      body: { isActive },
    });
  },

  // Revoke all sessions for a user
  revokeSessions: async (id) => {
    return apiRequest(`/auth/admin/users/${id}/revoke-sessions`, {
      method: "POST",
    });
  },

  // Lock user account
  lock: async (id, reason, lockDuration) => {
    return apiRequest(`/auth/admin/users/${id}/lock`, {
      method: "POST",
      body: { reason, lockDuration },
    });
  },

  // Unlock user account
  unlock: async (id, reason) => {
    return apiRequest(`/auth/admin/users/${id}/unlock`, {
      method: "POST",
      body: { reason },
    });
  },
};

// ==================== Admin API ====================

export const adminAPI = {
  // Get admin dashboard statistics
  getDashboardStats: async () => {
    return apiRequest("/admin/dashboard/stats");
  },

  // Admin company management
  getAllCompanies: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/companies${query ? `?${query}` : ""}`);
  },

  getCompanyById: async (id) => {
    return apiRequest(`/admin/companies/${id}`);
  },

  // Admin draft management
  getAllDrafts: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/drafts${query ? `?${query}` : ""}`);
  },

  getDraftById: async (id) => {
    return apiRequest(`/admin/drafts/${id}`);
  },
};

// ==================== Audit Logs API ====================
//
// Cursor-paginated. The list endpoint returns { rows, nextCursor, hasMore }.
// Filters narrow the index range — they don't degrade pagination.

export const auditLogsAPI = {
  // List with cursor pagination. `filters` is the object passed straight
  // through to the server (cursor, limit, actionCategory, action, actorId,
  // targetId, requestId, dateFrom, dateTo, success, q).
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.append(k, v);
    });
    const query = params.toString();
    return apiRequest(`/admin/audit-logs${query ? `?${query}` : ""}`);
  },

  getById: async (id) => {
    return apiRequest(`/admin/audit-logs/${id}`);
  },

  // Grouped-by-action aggregation. Default window: last 30 days. Returns
  // one row per unique action with count + first/last seen + distinct
  // actors/targets. Same filter shape as getAll.
  getGrouped: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.append(k, v);
    });
    const query = params.toString();
    return apiRequest(`/admin/audit-logs/grouped${query ? `?${query}` : ""}`);
  },

  // All rows sharing a requestId. Used by "View all events from this request"
  // in the detail modal.
  getByRequest: async (requestId) => {
    return apiRequest(
      `/admin/audit-logs/by-request/${encodeURIComponent(requestId)}`,
    );
  },

  // Last N events for an entity. Powers the embedded EntityAuditHistory
  // panel on User/Company/Order detail modals.
  getByEntity: async ({ type, id, limit = 20 } = {}) => {
    const params = new URLSearchParams({ id, limit });
    if (type) params.set("type", type);
    return apiRequest(`/admin/audit-logs/by-entity?${params.toString()}`);
  },

  // Walks the hash chain. Returns { ok, scanned, brokenAt, brokenReason, durationMs, verifiedAt }.
  verifyIntegrity: async () => {
    return apiRequest("/admin/audit-logs/verify");
  },

  // Streamed CSV download. We can't use a plain <a href> here — the API
  // requires a Bearer token in the Authorization header, which a navigation
  // request doesn't carry. Instead we fetch with auth, read the streamed
  // body as a Blob, and synthesize a temporary object-URL download.
  downloadCsv: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.append(k, v);
    });
    const base =
      (typeof window !== "undefined" && window.__API_BASE__) ||
      process.env.REACT_APP_API_URL ||
      "";
    const qs = params.toString();
    const url = `${base}/admin/audit-logs/export${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, { headers: getHeaders(true) });
    if (!response.ok) {
      throw new Error(`Audit CSV export failed (${response.status})`);
    }
    const blob = await response.blob();
    const fileUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(fileUrl);
  },

  // Fire-and-forget download tracking. Called from useDocumentExport on
  // each export trigger. Never blocks the user — failures are silent.
  trackDownload: async ({
    resourceType,
    resourceId,
    format,
    surface,
    meta,
  } = {}) => {
    try {
      return await apiRequest("/audit/track-download", {
        method: "POST",
        body: { resourceType, resourceId, format, surface, meta },
      });
    } catch (err) {
      // Silent failure — the user's download succeeds either way.
      return { success: false, error: err?.message };
    }
  },
};

// ==================== Error Logs API ====================
//
// Mirrors auditLogsAPI but for the errorlogs collection. The list endpoint
// uses cursor pagination; the grouped endpoint runs a server-side
// fingerprint aggregation for the Sentry-style summary view.

export const errorLogsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.append(k, v);
    });
    const query = params.toString();
    return apiRequest(`/admin/error-logs${query ? `?${query}` : ""}`);
  },

  // Grouped by fingerprint (default window: last 30 days; server enforces).
  // Returns one row per unique bug, with count + firstSeen/lastSeen +
  // affectedUsersCount + affectedRoutes.
  getGrouped: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.append(k, v);
    });
    const query = params.toString();
    return apiRequest(`/admin/error-logs/grouped${query ? `?${query}` : ""}`);
  },

  getById: async (id) => apiRequest(`/admin/error-logs/${id}`),

  // Cross-link from AuditLogDetailModal "Related errors" section.
  getByRequest: async (requestId) =>
    apiRequest(`/admin/error-logs/by-request/${encodeURIComponent(requestId)}`),

  // All occurrences of a single fingerprint — used when the user clicks a
  // grouped row and wants per-event detail.
  getByFingerprint: async (fingerprint, limit = 50) => {
    const params = new URLSearchParams({ limit });
    return apiRequest(
      `/admin/error-logs/by-fingerprint/${encodeURIComponent(fingerprint)}?${params.toString()}`,
    );
  },

  // Auth-aware streamed CSV download — same pattern as auditLogsAPI.downloadCsv.
  // See that method's comment for why we can't use a plain <a href>.
  downloadCsv: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.append(k, v);
    });
    const base =
      (typeof window !== "undefined" && window.__API_BASE__) ||
      process.env.REACT_APP_API_URL ||
      "";
    const qs = params.toString();
    const url = `${base}/admin/error-logs/export${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, { headers: getHeaders(true) });
    if (!response.ok) {
      throw new Error(`Error CSV export failed (${response.status})`);
    }
    const blob = await response.blob();
    const fileUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = `error-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(fileUrl);
  },

  // Fire-and-forget client error reporter. Called from utils/errorReporter
  // when a browser-side error needs to be persisted server-side.
  reportClient: async ({
    name,
    message,
    stack,
    url,
    lineno,
    colno,
    componentStack,
    currentRoute,
  } = {}) => {
    try {
      return await apiRequest("/errors/report", {
        method: "POST",
        body: {
          name,
          message,
          stack,
          url,
          lineno,
          colno,
          componentStack,
          currentRoute,
        },
      });
    } catch (err) {
      // Silent — never escalate an error-report failure into a user-visible error.
      return { success: false, error: err?.message };
    }
  },
};

// ==================== User Flags API ====================

export const userFlagsAPI = {
  // Any admin can create a flag against another user.
  create: async (userId, reason) => {
    return apiRequest(`/admin/users/${userId}/flag`, {
      method: "POST",
      body: { reason },
    });
  },

  // List all flags ever filed against a specific user (open + resolved).
  listForUser: async (userId) => {
    return apiRequest(`/admin/users/${userId}/flags`);
  },

  // Super-admin only: org-wide flag inbox.
  listOpen: async (status = "open") => {
    const params = new URLSearchParams({ status });
    return apiRequest(`/admin/flags?${params.toString()}`);
  },

  // Super-admin only: clear a flag (status -> 'cleared').
  clear: async (flagId, notes) => {
    return apiRequest(`/admin/flags/${flagId}/clear`, {
      method: "POST",
      body: { notes },
    });
  },

  // Super-admin only: escalate to compromised (also deactivates user,
  // revokes all tokens, sets compromisedAt). Writes 3 audit rows under
  // the same requestId so they correlate.
  markCompromised: async (flagId, notes) => {
    return apiRequest(`/admin/flags/${flagId}/mark-compromised`, {
      method: "POST",
      body: { notes },
    });
  },
};

// ==================== Roles API ====================

export const rolesAPI = {
  // Get all roles
  getAll: async () => {
    return apiRequest("/roles");
  },

  // Get role by ID
  getById: async (id) => {
    return apiRequest(`/roles/${id}`);
  },

  // Create role
  create: async (roleData) => {
    return apiRequest("/roles", {
      method: "POST",
      body: roleData,
    });
  },

  // Update role
  update: async (id, roleData) => {
    return apiRequest(`/roles/${id}`, {
      method: "PUT",
      body: roleData,
    });
  },

  // Delete role
  delete: async (id) => {
    return apiRequest(`/roles/${id}`, { method: "DELETE" });
  },

  // Get all permissions
  getPermissions: async () => {
    return apiRequest("/roles/permissions");
  },
};

export const rocAPI = {
  searchCompany: async (queryText) => {
    return apiRequest("/roc/search", {
      method: "POST",
      body: { companyName: queryText },
    });
  },
  fetchCompanyMasterData: async (cinOrName) => {
    return rocAPI.searchCompany(cinOrName);
  },
};

export const mcaAPI = {
  fetchCompanyByCinOrLlpin: async (cinOrName) => {
    const params = new URLSearchParams({ name: cinOrName });
    return apiRequest(`/company/search?${params.toString()}`);
  },
};

// ==================== AI API (Chat & Generation) ====================

// ==================== Minutes Page Ledger API ====================

export const minutesLedgerAPI = {
  /**
   * Read the page-continuity view for a (company, meetingType, FY) scope — what page
   * the next set of minutes will start on. Non-mutating; backend authorizes per-user.
   */
  getLedger: async ({ companyCin, meetingType, committeeKey, meetingDate } = {}) => {
    const params = new URLSearchParams();
    if (companyCin) params.set("companyCin", companyCin);
    if (meetingType) params.set("meetingType", meetingType);
    if (committeeKey) params.set("committeeKey", committeeKey);
    if (meetingDate) params.set("meetingDate", meetingDate);
    const qs = params.toString();
    return apiRequest(`/ai/meetings/minutes/ledger${qs ? `?${qs}` : ""}`);
  },

  /**
   * Render the current minutes to a continuously-numbered PDF and download it.
   * Resolves with the allocated range read from the X-Minutes-* response headers.
   */
  downloadNumberedPdf: async ({
    title,
    bodyHtml,
    companyCin,
    meetingType,
    committeeKey,
    meetingDate,
    meetingNumber,
    draftId,
    allocationKey,
    startPageOverride,
    filename,
  }) => {
    const payload = JSON.stringify({
      title,
      bodyHtml,
      companyCin,
      meetingType,
      committeeKey,
      meetingDate,
      meetingNumber,
      draftId,
      allocationKey,
      startPageOverride,
    });
    // Raw fetch is needed for the binary blob + X-Minutes-* headers, but mirror
    // apiRequest's auth handling: on an expired access token (401) refresh once
    // (single-flight, via the HttpOnly cookie) and retry — otherwise a long editing
    // session dead-ends this one button with "Token expired" while every other action
    // silently refreshes. Token is read inside doFetch so the retry uses the new one.
    const doFetch = () => {
      const token = tokenManager.getToken();
      return fetch(`${API_BASE_URL}/ai/meetings/minutes/pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: payload,
      });
    };
    let response = await doFetch();
    if (response.status === 401 && tokenManager.getToken()) {
      const refreshed = await refreshAccessToken();
      if (refreshed) response = await doFetch();
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || "Numbered PDF export failed",
        error.code || "PDF_ERROR",
        response.status
      );
    }
    const blob = await response.blob();
    const range = {
      startPage: Number(response.headers.get("X-Minutes-Start-Page")) || null,
      endPage: Number(response.headers.get("X-Minutes-End-Page")) || null,
      pageCount: Number(response.headers.get("X-Minutes-Page-Count")) || null,
      financialYear: response.headers.get("X-Minutes-Financial-Year") || "",
      numbered: response.headers.get("X-Minutes-Numbered") === "true",
      reissued: response.headers.get("X-Minutes-Reissued") === "true",
    };
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename || "meeting-minutes"}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Delay revocation so the browser has time to initiate the download;
    // revoking synchronously can silently cancel the save on some browsers.
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return { success: true, ...range };
  },

  /**
   * Render the current minutes to a continuously-numbered, editable .docx and download it.
   * Shares the same financial-year page ledger as the PDF (same allocationKey scope), so
   * the .docx numbering matches the PDF. Sends the `meeting`/`companyContext` payload so
   * the server can regenerate the structured draft (cache hit — no new AI call).
   * Resolves with the allocated range read from the X-Minutes-* response headers.
   */
  downloadNumberedDocx: async ({
    title,
    bodyHtml,
    meeting,
    companyContext,
    existingText,
    language,
    referenceDocument,
    companyCin,
    meetingType,
    committeeKey,
    meetingDate,
    meetingNumber,
    draftId,
    allocationKey,
    startPageOverride,
    filename,
  }) => {
    const token = tokenManager.getToken();
    const response = await fetch(`${API_BASE_URL}/ai/meetings/minutes/docx`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        title,
        bodyHtml,
        meeting,
        companyContext,
        existingText,
        language,
        referenceDocument,
        companyCin,
        meetingType,
        committeeKey,
        meetingDate,
        meetingNumber,
        draftId,
        allocationKey,
        startPageOverride,
      }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || "Numbered DOCX export failed",
        error.code || "DOCX_ERROR",
        response.status
      );
    }
    const blob = await response.blob();
    const range = {
      startPage: Number(response.headers.get("X-Minutes-Start-Page")) || null,
      endPage: Number(response.headers.get("X-Minutes-End-Page")) || null,
      pageCount: Number(response.headers.get("X-Minutes-Page-Count")) || null,
      financialYear: response.headers.get("X-Minutes-Financial-Year") || "",
      numbered: response.headers.get("X-Minutes-Numbered") === "true",
      reissued: response.headers.get("X-Minutes-Reissued") === "true",
    };
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename || "meeting-minutes"}.docx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { success: true, ...range };
  },
};

// ==================== Draft Sessions API (studio bake-off Prototype A) ====================
// The server-side shared working draft the AI chat and the canvas studio edit
// together. Optimistic concurrency: send baseRev; a 409 means the other writer
// got there first (err.details.session carries the current state).
// PROTOTYPE: remove if Prototype B wins.

export const draftSessionsAPI = {
  getActive: async (companyCin, kind = 'minutes') =>
    apiRequest(`/ai/draft-sessions/active?companyCin=${encodeURIComponent(companyCin)}&kind=${encodeURIComponent(kind)}`),

  create: async (body) =>
    apiRequest('/ai/draft-sessions', { method: 'POST', body }),

  patch: async (id, body) =>
    apiRequest(`/ai/draft-sessions/${id}`, { method: 'PATCH', body }),
};

/**
 * Render any draft to a clean A4 PDF server-side and download it.
 *
 * The browser's own Print dialog stamps the page URL, a timestamp and "1/2"
 * outside the page box, where no stylesheet can remove them — unacceptable on a
 * certified true copy. This route returns the same document without them.
 *
 * Throws with code 'PDF_DISABLED' (501) when the headless renderer isn't
 * available, which callers treat as "fall back to browser print".
 */
export const documentPdfAPI = {
  download: async ({ title, bodyHtml, kind, cin, filename }) => {
    const payload = JSON.stringify({ title, bodyHtml, kind, cin });
    // Raw fetch for the binary blob, mirroring apiRequest's single-flight 401
    // refresh so a long editing session doesn't dead-end on this one button.
    const doFetch = () => {
      const token = tokenManager.getToken();
      return fetch(`${API_BASE_URL}/ai/documents/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: payload,
      });
    };
    let response = await doFetch();
    if (response.status === 401 && tokenManager.getToken()) {
      const refreshed = await refreshAccessToken();
      if (refreshed) response = await doFetch();
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(
        error.message || 'PDF export failed',
        error.code || 'PDF_ERROR',
        response.status,
      );
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename || 'document'}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Delay revocation — revoking synchronously can cancel the save on some browsers.
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return { success: true };
  },
};

export const aiAPI = {
  /**
   * Send a message to the ComplyV AI compliance assistant.
   * Backend proxies this to the configured AI provider.
   */
  chat: async (messages, context = {}, opts = {}) => {
    return apiRequest("/ai/chat", {
      method: "POST",
      // consent + sessionId are optional; absent → ephemeral (nothing persisted).
      // clientTurnId lets a reloaded browser recover this exact reply.
      body: { messages, context, consent: opts.consent, sessionId: opts.sessionId, clientTurnId: opts.clientTurnId },
    });
  },

  /** Execute an agent action the user confirmed on a proposal card (Phase 3). */
  executeChatAction: async ({ action, params, idempotencyKey }) => {
    return apiRequest("/ai/chat/actions/execute", {
      method: "POST",
      body: { action, params, idempotencyKey },
    });
  },

  /**
   * List the user's saved conversations (consent-gated; newest first).
   * Pass { deleted: true } for the Recently Deleted bin instead, and/or
   * { draftKind: 'minutes' | 'report' | 'resolution' | 'document' } to scope
   * to conversations that produced that kind of document (per-page history).
   */
  listChatSessions: async (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.deleted) params.set("deleted", "1");
    if (opts.draftKind) params.set("draftKind", opts.draftKind);
    const qs = params.toString();
    return apiRequest(`/ai/chat/sessions${qs ? `?${qs}` : ""}`);
  },

  /** Load one saved conversation's messages. */
  getChatSession: async (id) => apiRequest(`/ai/chat/sessions/${id}`),

  /**
   * Delete one conversation. Default: soft delete → Recently Deleted (restorable
   * ~7 days). Pass { permanent: true } to erase it immediately (DPDP).
   */
  deleteChatSession: async (id, opts = {}) =>
    apiRequest(`/ai/chat/sessions/${id}${opts.permanent ? "?permanent=1" : ""}`, { method: "DELETE" }),

  /** Restore a soft-deleted conversation from Recently Deleted. */
  restoreChatSession: async (id) =>
    apiRequest(`/ai/chat/sessions/${id}/restore`, { method: "POST" }),

  /** One persisted chat image (owner-scoped) — cross-device thumbnail restore. */
  getChatMedia: async (attachmentId) =>
    apiRequest(`/ai/chat/media/${encodeURIComponent(attachmentId)}`),

  /**
   * Persist a sent document attachment (PDF/DOCX/TXT) for cross-device
   * restore — the document twin of the chat image path. Called fire-and-forget
   * after a send, ONLY when history consent is on.
   */
  uploadChatAttachment: async (file, { attachmentId, sessionId } = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("attachmentId", attachmentId);
    if (sessionId) formData.append("sessionId", sessionId);
    // Raw fetch like every other multipart method here — apiRequest forces
    // Content-Type: application/json, which strips the multipart boundary
    // and the backend never sees the file.
    const response = await fetch(`${API_BASE_URL}/ai/chat/attachments`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
      body: formData,
    });
    return handleResponse(response);
  },

  /**
   * Where to fetch a persisted document from: a short-lived signed S3 URL, or
   * a same-origin /raw path (use getChatAttachmentBlob for that one — it needs
   * the auth header a plain <object>/fetch won't carry).
   */
  getChatAttachmentUrl: async (attachmentId) =>
    apiRequest(`/ai/chat/attachments/${encodeURIComponent(attachmentId)}/url`),

  /**
   * The persisted document's bytes as a Blob via the authed /raw route — the
   * fallback when there is no signed URL (local storage dev mode).
   */
  getChatAttachmentBlob: async (attachmentId) => {
    const token = tokenManager.getToken();
    const res = await fetch(`${API_BASE_URL}/ai/chat/attachments/${encodeURIComponent(attachmentId)}/raw`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`attachment fetch failed (${res.status})`);
    return res.blob();
  },

  /**
   * A reply as a Word or PDF file, with the as-on date and sources set out.
   * The server builds it from the text sent and stores nothing.
   */
  exportAnswer: async ({ format, message, title }) => {
    const token = tokenManager.getToken();
    const res = await fetch(`${API_BASE_URL}/ai/chat/export`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ format, message, title }),
    });
    if (!res.ok) throw new Error(`export failed (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = String(title || "vee-answer").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "vee-answer";
    a.href = url;
    a.download = `${safe}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /** Delete the user's entire chat history (DPDP erasure — permanent). */
  deleteAllChatHistory: async () => apiRequest("/ai/chat/history", { method: "DELETE" }),

  /**
   * Thumbs feedback on a reply. Volunteered per-click; the exchange text is
   * redacted server-side before storage (ImprovementSample kind 'feedback').
   */
  sendChatFeedback: async ({ sessionId, verdict, reasons, userText, assistantText }) =>
    apiRequest("/ai/chat/feedback", {
      method: "POST",
      body: { sessionId, verdict, reasons, userText, assistantText },
    }),

  /**
   * Streaming chat (SSE over fetch). Calls handlers.onToken(text) / onStatus(data)
   * as events arrive and resolves with the final `done` envelope. Throws on a
   * non-OK response so callers can fall back to the non-streaming chat().
   */
  chatStream: async (messages, context = {}, opts = {}, handlers = {}) => {
    const token = tokenManager.getToken();
    const res = await fetch(`${API_BASE_URL}/ai/chat/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      // clientTurnId rides along so a reloaded browser can recover this reply.
      // signal lets the caller abort an in-flight stream ("Stop generating").
      body: JSON.stringify({ messages, context, consent: opts.consent, sessionId: opts.sessionId, clientTurnId: opts.clientTurnId }),
      signal: opts.signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`chat stream failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let doneEnvelope = null;

    const handle = (event, data) => {
      if (event === "token" && handlers.onToken) handlers.onToken(data.text || "");
      else if (event === "status" && handlers.onStatus) handlers.onStatus(data);
      else if (event === "done") doneEnvelope = data;
      else if (event === "error" && handlers.onError) handlers.onError(data);
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() || "";
      for (const chunk of chunks) {
        let event = "message";
        let dataStr = "";
        for (const line of chunk.split("\n")) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
        }
        if (!dataStr) continue;
        try { handle(event, JSON.parse(dataStr)); } catch (_e) { /* skip malformed */ }
      }
    }
    return doneEnvelope;
  },

  /**
   * Draft meeting minutes for the new-ui Meeting Minutes pipeline.
   * @param {object} meeting - full meeting config (type, date, venue, agenda items, participants, etc.)
   * @param {object} companyContext - canonical CompanyContext
   * @param {object} options - { useCache, existingText, referenceDocument }
   */
  draftMeetingMinutes: async (meeting, companyContext = {}, options = {}) => {
    return apiRequest("/ai/meetings/minutes/draft", {
      method: "POST",
      body: {
        meeting,
        companyContext,
        useCache: options?.useCache !== false,
        existingText: options?.existingText || null,
        referenceDocument: options?.referenceDocument || null,
      },
    });
  },

  /**
   * Upload a reference document (.txt or .docx) and get back the parsed structure.
   * Returns { success, data: { reference: { filename, rawText, agendaHeader, bodyParagraphs,
   *   resolvedClauses, furtherResolvedClauses } } }
   */
  parseReferenceDocument: async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(
      `${API_BASE_URL}/ai/reference-documents/parse`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
        body: formData,
      },
    );

    return handleResponse(response);
  },

  /**
   * Extract text from supporting documents (DIN letters, PAN cards, etc.).
   * Buffers are not persisted to disk / S3 / DB; AI usage logs may record
   * the prompt content for monitoring.
   * Accepts: .pdf, .jpg, .jpeg, .png, .txt, .docx (max 2 files, 15 MB each).
   *
   * @param {File[]} files - Array of File objects from file input
   * @returns {{ success, data: { documents: Array<{ filename, extractedText, fileType, byteSize, error? }> } }}
   */
  extractSupportingDocuments: async (files, opts = {}) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    // Optional higher text cap (the AI chat passes this so multi-page docs aren't
    // clipped as aggressively as the resolution-draft default).
    if (opts.maxChars) formData.append("maxChars", String(opts.maxChars));

    const response = await fetch(
      `${API_BASE_URL}/ai/supporting-documents/extract`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenManager.getToken()}` },
        body: formData,
      },
    );

    return handleResponse(response);
  },

  /**
   * Draft or improve a resolution.
   */
  draftResolution: async (
    topic,
    existingText = null,
    companyContext = {},
    options = {},
  ) => {
    const endpoint = USE_NEW_UI_RESOLUTION_PIPELINE
      ? "/ai/resolutions/draft"
      : "/ai/generate/resolution";

    const useCache = options?.useCache !== false;

    return apiRequest(endpoint, {
      method: "POST",
      body: {
        topic,
        existingText,
        companyContext,
        useCache,
        resolutionId: options?.resolutionId || null,
        referenceDocument: options?.referenceDocument || null,
        supportingDocuments: options?.supportingDocuments || [],
        // R5 — user-picked date (ISO "YYYY-MM-DD"); the backend pins the
        // draft's meetingDate to it instead of defaulting to today.
        meetingDate: options?.meetingDate || null,
        // T7 — 'board' | 'ordinary' | 'special', enforced server-side.
        resolutionKind: options?.resolutionKind || null,
        // Optional free-text steer from the AI Draft modal.
        instructions: options?.instructions || null,
      },
    });
  },

  /**
   * Draft a legal document (privacy policy, consent form, etc.).
   */
  draftDocument: async (documentType, companyContext = {}, options = {}) => {
    const preferredEndpoint = USE_NEW_UI_DOCUMENT_PIPELINE
      ? "/ai/documents/draft"
      : "/ai/generate/document";

    const requestBody = {
      documentType,
      companyContext,
      useCache: options?.useCache !== false,
      section: options?.section,
      category: options?.category,
      documentId: options?.documentId,
      existingText: options?.existingText || null,
      language: options?.language,
      referenceDocument: options?.referenceDocument || null,
      // Optional free-text steer from the AI Draft modal.
      instructions: options?.instructions || null,
    };

    try {
      return await apiRequest(preferredEndpoint, {
        method: "POST",
        body: requestBody,
      });
    } catch (error) {
      if (!USE_NEW_UI_DOCUMENT_PIPELINE || error?.status !== 404) {
        throw error;
      }

      return apiRequest("/ai/generate/document", {
        method: "POST",
        body: requestBody,
      });
    }
  },

  /**
   * Review and improve an uploaded resolution or document.
   */
  reviewDocument: async (text, documentType = "resolution") => {
    const endpoint = USE_NEW_UI_RESOLUTION_PIPELINE
      ? "/ai/resolutions/review"
      : "/ai/review";

    return apiRequest(endpoint, {
      method: "POST",
      body: { text, documentType },
    });
  },

  /**
   * Draft the bespoke (AI-fillable) sections of an Auditor's Report.
   * The deterministic client engine owns layout and merges the returned text.
   * @param {object} report - { reportTypeId, sections: string[], companyType, framework, opinionType, config }
   * @param {object} companyContext - canonical CompanyContext
   * @param {object} options - { useCache }
   * @returns {{ success, data: { source, sections, content, telemetry } }}
   */
  draftAuditorReportSections: async (
    report,
    companyContext = {},
    options = {},
  ) => {
    return apiRequest("/ai/reports/sections/draft", {
      method: "POST",
      body: {
        report,
        companyContext,
        useCache: options?.useCache !== false,
      },
    });
  },
};

// ==================== Portfolio / Compliance API ====================

export const portfolioAPI = {
  /**
   * Get compliance summary for the user's company portfolio.
   */
  getSummary: async () => {
    return apiRequest("/portfolio/summary");
  },

  /**
   * Get critical alerts across the portfolio.
   */
  getAlerts: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/portfolio/alerts${query ? `?${query}` : ""}`);
  },

  /**
   * Get filing progress for the current financial year.
   */
  getFilingProgress: async (fy = null) => {
    const query = fy ? `?fy=${fy}` : "";
    return apiRequest(`/portfolio/filing-progress${query}`);
  },

  /**
   * Get compliance calendar events.
   */
  getCalendarEvents: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/portfolio/calendar${query ? `?${query}` : ""}`);
  },

  /**
   * Get annual filing status per company.
   */
  getAnnualFilingStatus: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/portfolio/annual-filing${query ? `?${query}` : ""}`);
  },

  /**
   * Get reports and analytics data.
   */
  getReports: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/portfolio/reports${query ? `?${query}` : ""}`);
  },
};

// ==================== AGM / Deadline Calculator API ====================

export const agmAPI = {
  /**
   * Calculate filing deadlines from AGM date.
   * Can also be computed client-side; API version stores calculations.
   */
  calculateDeadlines: async (
    agmDate,
    entityType = "pvtlimited",
    fyEnd = "03-31",
  ) => {
    return apiRequest("/agm/calculate", {
      method: "POST",
      body: { agmDate, entityType, fyEnd },
    });
  },

  /**
   * Save AGM deadline calculation for a company.
   */
  saveCalculation: async (companyId, calculationData) => {
    return apiRequest("/agm/save", {
      method: "POST",
      body: { companyId, ...calculationData },
    });
  },
};

// ==================== Marketplace API ====================

export const marketplaceAPI = {
  /**
   * Get all active listings (for sale, buyer wanted, merger/JV).
   */
  getListings: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/marketplace/listings${query ? `?${query}` : ""}`, {
      includeAuth: false,
    });
  },

  /**
   * Submit a company for sale or partnership.
   */
  createListing: async (listingData) => {
    return apiRequest("/marketplace/listings", {
      method: "POST",
      body: listingData,
    });
  },

  /**
   * Register interest in a listing (initiates NDA process).
   */
  submitInterest: async (listingId, interestData) => {
    return apiRequest(`/marketplace/listings/${listingId}/interest`, {
      method: "POST",
      body: interestData,
    });
  },

  /**
   * Register as a buyer with preferences.
   */
  registerBuyer: async (buyerData) => {
    return apiRequest("/marketplace/buyers", {
      method: "POST",
      body: buyerData,
    });
  },
};

// ==================== Resolutions API ====================

export const resolutionsAPI = {
  /**
   * Get all resolution templates.
   */
  getTemplates: async () => {
    return apiRequest("/resolutions/templates", { includeAuth: false });
  },

  /**
   * Save a drafted resolution for a company.
   */
  saveDraft: async (draftData) => {
    return apiRequest("/resolutions/drafts", {
      method: "POST",
      body: draftData,
    });
  },

  /**
   * Get saved drafts for the user.
   */
  getDrafts: async () => {
    return apiRequest("/resolutions/drafts");
  },
};

// ==================== Companies API ====================

export const companiesAPI = {
  /**
   * Get all companies for the authenticated user
   */
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/companies${query ? `?${query}` : ""}`);
  },
  // Get company by CIN
  getByCin: async (cin) => {
    return apiRequest(`/companies/cin/${cin}`);
  },
  /**
   * Compare a stored company against the MCA master dataset.
   * Returns { differences: [{ field, label, current, incoming }], inSync,
   * notPublishedByMca }. Read-only — apply what the user accepts with update().
   */
  mcaDiff: async (id) => {
    return apiRequest(`/companies/${id}/mca-diff`);
  },
  /**
   * Get a single company by ID
   */
  getById: async (id) => {
    return apiRequest(`/companies/${id}`);
  },

  /**
   * Create a new company
   */
  create: async (companyData) => {
    return apiRequest("/companies", {
      method: "POST",
      body: companyData,
    });
  },

  /**
   * Update a company
   */
  update: async (id, companyData) => {
    return apiRequest(`/companies/${id}`, {
      method: "PUT",
      body: companyData,
    });
  },

  /**
   * Delete a company
   */
  delete: async (id) => {
    return apiRequest(`/companies/${id}`, { method: "DELETE" });
  },

  /**
   * Mark one annual form filed / not filed for a FY cycle.
   * `formKey` is a FORM_REGISTRY key (aoc4, mgt7, …), `cycleKey` a FY key
   * like "2024-25" (fyKey() in the deadline engine). Deliberately separate
   * from update() so a company payload can never clobber the flags.
   *
   * `filed` is three-state:
   *   true  — recorded filed (manual; outranks any MCA-derived flag)
   *   false — recorded NOT filed (also manual, also outranks MCA — an explicit
   *           "no" must survive the next registry fetch)
   *   null  — clear the manual note, letting an MCA-derived flag show through
   *
   * `srn` / `filedOn` are OPTIONAL evidence and only apply to `filed: true`.
   * They follow present-key-wins semantics: pass `srn: ''` to CLEAR a stored
   * SRN, omit the key to leave it untouched. That is why they are spread in
   * conditionally rather than always sent — an always-present `srn: undefined`
   * would serialize away and read as "omitted" by luck rather than by design.
   *
   * The server never rejects an odd SRN; it returns `srnLooksValid: false` so
   * the caller can hint. It DOES reject an unparseable or future `filedOn`.
   */
  setFilingStatus: async (id, { formKey, cycleKey, filed, srn, filedOn }) => {
    return apiRequest(`/companies/${id}/filings`, {
      method: "PATCH",
      body: {
        formKey,
        cycleKey,
        filed,
        ...(srn !== undefined ? { srn } : {}),
        ...(filedOn !== undefined ? { filedOn } : {}),
      },
    });
  },

  /**
   * Fetch MCA master data for this company's stored CIN and apply the
   * registry-sourced fields server-side.
   *
   * Unlike mcaDiff() this WRITES — but only the backend's field whitelist, and
   * only fields the payload actually published (an absent field leaves the
   * stored value alone, it is never blanked). Hand-entered fields (auditor,
   * notes, contactPerson, …) and the filed flags are never touched: manual
   * filing records keep outranking registry-derived ones in both polarities.
   *
   * This is the only writer of the `mcaDataAsOf` provenance stamp, which is why
   * the fetch happens on the server — the client never gets to decide what
   * counts as registry-sourced.
   *
   * @returns {Promise<{ data, applied: Array<{field,label,from,to}>,
   *   notPublished: string[], mcaDataAsOf: string, xbrl }>}
   *   Rejects with code MCA_COMPANY_NOT_FOUND (not in the dataset) or
   *   MCA_SOURCE_UNAVAILABLE (couldn't reach it); neither writes anything.
   */
  refreshFromMca: async (id) => {
    return apiRequest(`/companies/${id}/mca-refresh`, { method: "POST" });
  },

  /**
   * Record the AOC-4 XBRL inputs MCA master data cannot supply.
   * Listing status and paid-up capital already come from the registry and
   * settle most companies; this carries only the remainder.
   *
   * @param {object} patch
   *   turnoverAbove100Cr  '' | 'yes' | 'no'   — the single optional question;
   *     `''` un-answers it and returns the determination to "not determined"
   *   xbrlFiledPreviously boolean             — sticky: once in XBRL, always.
   *     `false` is accepted and is the correction path for a mistaken entry —
   *     the rule binds companies, not typos.
   *   xbrlOverride        '' | 'required' | 'not_required' — manual override
   */
  setXbrl: async (id, patch) => {
    return apiRequest(`/companies/${id}/xbrl`, {
      method: "PATCH",
      body: patch,
    });
  },

  /**
   * Company access management — owner-only. `list`/`collaborators` read;
   * `approve`/`deny`/`revoke`/`setLevel` act on a specific grant.
   */
  access: {
    list: (companyId) => apiRequest(`/companies/${companyId}/access`),
    collaborators: (companyId) =>
      apiRequest(`/companies/${companyId}/collaborators`),
    // Owner-scoped: every pending/active grant across companies you own.
    requests: () => apiRequest("/companies/access/requests"),
    // Requester-scoped: every grant the current user requested, any company.
    myRequests: () => apiRequest("/companies/access/my-requests"),
    approve: (companyId, grantId) =>
      apiRequest(`/companies/${companyId}/access/${grantId}/approve`, {
        method: "POST",
      }),
    deny: (companyId, grantId) =>
      apiRequest(`/companies/${companyId}/access/${grantId}/deny`, {
        method: "POST",
      }),
    revoke: (companyId, grantId) =>
      apiRequest(`/companies/${companyId}/access/${grantId}/revoke`, {
        method: "POST",
      }),
    // Requester nudges the owner about a still-pending request (rate-limited).
    remind: (companyId, grantId) =>
      apiRequest(`/companies/${companyId}/access/${grantId}/remind`, {
        method: "POST",
      }),
    setLevel: (companyId, grantId, accessLevel) =>
      apiRequest(`/companies/${companyId}/access/${grantId}`, {
        method: "PATCH",
        body: { accessLevel },
      }),
  },

  /**
   * Get company statistics
   */
  getStats: async () => {
    return apiRequest("/companies/stats");
  },

  /**
   * Get Pvt Limited company specific statistics
   */
  getPvtLimitedStats: async () => {
    return apiRequest("/companies/pvt-limited-stats");
  },

  /**
   * Get LLP company specific statistics
   */
  getLLPStats: async () => {
    return apiRequest("/companies/llp-stats");
  },

  /**
   * Get OPC company specific statistics
   */
  getOPCStats: async () => {
    return apiRequest("/companies/opc-stats");
  },

  /**
   * Get Public company specific statistics
   */
  getPublicCoStats: async () => {
    return apiRequest("/companies/public-co-stats");
  },

  /**
   * Get NGO company specific statistics
   */
  getNGOStats: async () => {
    return apiRequest("/companies/ngo-stats");
  },

  /**
   * Director operations
   */
  addDirector: async (companyId, directorData) => {
    return apiRequest(`/companies/${companyId}/directors`, {
      method: "POST",
      body: directorData,
    });
  },

  updateDirector: async (companyId, directorId, directorData) => {
    return apiRequest(`/companies/${companyId}/directors/${directorId}`, {
      method: "PUT",
      body: directorData,
    });
  },

  removeDirector: async (companyId, directorId) => {
    return apiRequest(`/companies/${companyId}/directors/${directorId}`, {
      method: "DELETE",
    });
  },
};

// ==================== Drafts API (Document History) ====================

export const draftsAPI = {
  /** Save or upsert a draft (by documentId + companyCin). */
  save: async (data) => {
    return apiRequest("/drafts", { method: "POST", body: data });
  },

  /** List user's drafts (paginated, filterable). */
  getAll: async (params = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ""),
    ).toString();
    return apiRequest(`/drafts${query ? `?${query}` : ""}`);
  },

  /** Get a single draft by ID (full content + minutesConfig). */
  getById: async (id) => {
    return apiRequest(`/drafts/${id}`);
  },

  /** Look up a draft by its human reference number (e.g. RES-4HJ8MN). */
  getByRef: async (ref) => {
    return apiRequest(`/drafts/by-ref/${encodeURIComponent(ref)}`);
  },

  /** Update an existing draft (partial update). */
  update: async (id, data) => {
    return apiRequest(`/drafts/${id}`, { method: "PUT", body: data });
  },

  /** Update an existing draft (alias for update method). */
  updateDraft: async (id, data) => {
    return apiRequest(`/drafts/${id}`, { method: "PUT", body: data });
  },

  /** Soft-delete a draft. */
  delete: async (id) => {
    return apiRequest(`/drafts/${id}`, { method: "DELETE" });
  },

  /** Soft-delete a draft (alias for delete method). */
  deleteDraft: async (id) => {
    return apiRequest(`/drafts/${id}`, { method: "DELETE" });
  },

  /** Share a draft with a professional (adds to the allowlist → restricted). */
  shareDraft: async (id, { userId, accessLevel }) => {
    return apiRequest(`/drafts/${id}/share`, {
      method: "POST",
      body: { userId, accessLevel },
    });
  },

  /** Remove a professional from a draft's share allowlist. */
  unshareDraft: async (id, userId) => {
    return apiRequest(`/drafts/${id}/share/${userId}`, { method: "DELETE" });
  },

  /** List the professionals a draft is shared with. */
  getDraftShares: async (id) => {
    return apiRequest(`/drafts/${id}/shares`);
  },

  /** Sign-off step: action is submit | approve | request_changes | sign. */
  signoff: async (id, { action, note }) => {
    return apiRequest(`/drafts/${id}/signoff`, {
      method: "POST",
      body: { action, note },
    });
  },
};

// ==================== Recipes API (My Templates) ====================
// Firm drafting recipes — created only via the chat propose→confirm flow
// (save_recipe); this API manages what already passed the save-time smoke test.

export const recipesAPI = {
  /** List the user's templates (paginated; status: active|archived|all). */
  getAll: async (params = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ""),
    ).toString();
    return apiRequest(`/recipes${query ? `?${query}` : ""}`);
  },

  /** Get a single template by ID. */
  getById: async (id) => {
    return apiRequest(`/recipes/${id}`);
  },

  /** Rename and/or archive/restore a template ({ name?, status? }). */
  update: async (id, data) => {
    return apiRequest(`/recipes/${id}`, { method: "PATCH", body: data });
  },

  /** Permanently delete a template. */
  remove: async (id) => {
    return apiRequest(`/recipes/${id}`, { method: "DELETE" });
  },
};

// ==================== Draft UPSI classification ====================

/**
 * Does a draft carry unpublished price sensitive information?
 *
 * Set by a person, never inferred. When a draft is marked `upsi` (or `unsure`),
 * sharing it raises a suggested entry for the company's SEBI register — the
 * compliance officer still supplies the legitimate purpose and confirms.
 *
 * status: 'unclassified' | 'not_upsi' | 'upsi' | 'unsure'
 */
export const draftUpsiAPI = {
  classify: async (draftId, { status, upsiEvent = null, note = "" }) =>
    apiRequest(`/drafts/${draftId}/upsi`, {
      method: "PATCH",
      body: { status, upsiEvent, note },
    }),
};

// ==================== SDD API (SEBI PIT Reg 3(5)/3(6)) ====================

/**
 * Structured Digital Database.
 *
 * Note what this object does NOT have: no `updateShare`, no `deleteShare`. The
 * register is append-only by law, so those endpoints do not exist server-side
 * either — a mistaken row is superseded via `correctShare`, which writes a new
 * row and leaves the original visible. Do not add them.
 *
 * Every call is scoped to one company by path.
 */
const sddQuery = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== ""),
  ).toString();
  return query ? `?${query}` : "";
};

export const sddAPI = {
  /**
   * The caller's own level and capability map for this company. Screens call
   * this first and hide what the server would refuse — presentation only; the
   * server is what actually enforces.
   */
  getMyAccess: async (companyId) => apiRequest(`/sdd/${companyId}/my-access`),

  /** Everyone's level, for the access screen. Compliance Officer only. */
  getAccessList: async (companyId) => apiRequest(`/sdd/${companyId}/access`),

  setAccess: async (companyId, { userId, level, note }) =>
    apiRequest(`/sdd/${companyId}/access`, { method: 'PUT', body: { userId, level, note } }),

  // --- The UPSI mailbox: one per company, connected by its compliance officer.
  getMailbox: async (companyId) => apiRequest(`/sdd/${companyId}/mailbox`),
  /** Password is required on first connection; blank on an update keeps the stored one. */
  setMailbox: async (companyId, settings) =>
    apiRequest(`/sdd/${companyId}/mailbox`, { method: 'PUT', body: settings }),
  testMailbox: async (companyId) => apiRequest(`/sdd/${companyId}/mailbox/test`, { method: 'POST' }),
  syncMailbox: async (companyId) => apiRequest(`/sdd/${companyId}/mailbox/sync`, { method: 'POST' }),

  // --- Sent-mail watches: a person's Sent folder, for UPSI nobody copied to the UPSI address.
  listSentWatches: async (companyId) => apiRequest(`/sdd/${companyId}/sent-watches`),
  /** Consent is required to create one; a blank password on an update keeps the stored one. */
  saveSentWatch: async (companyId, settings) =>
    apiRequest(`/sdd/${companyId}/sent-watches`, { method: 'PUT', body: settings }),
  testSentWatch: async (companyId, watchId) =>
    apiRequest(`/sdd/${companyId}/sent-watches/${watchId}/test`, { method: 'POST' }),
  syncSentWatch: async (companyId, watchId) =>
    apiRequest(`/sdd/${companyId}/sent-watches/${watchId}/sync`, { method: 'POST' }),
  removeSentWatch: async (companyId, watchId) =>
    apiRequest(`/sdd/${companyId}/sent-watches/${watchId}`, { method: 'DELETE' }),

  // --- Insiders (the people list) ---
  getInsiders: async (companyId, params = {}) =>
    apiRequest(`/sdd/${companyId}/insiders${sddQuery(params)}`),

  createInsider: async (companyId, data) =>
    apiRequest(`/sdd/${companyId}/insiders`, { method: "POST", body: data }),

  updateInsider: async (companyId, id, data) =>
    apiRequest(`/sdd/${companyId}/insiders/${id}`, { method: "PATCH", body: data }),

  /** Stand someone down. Never a delete — historical rows must keep resolving. */
  deactivateInsider: async (companyId, id, effectiveTo) =>
    apiRequest(`/sdd/${companyId}/insiders/${id}/deactivate`, {
      method: "POST",
      body: { effectiveTo },
    }),

  // --- UPSI events (the information) ---
  getEvents: async (companyId, params = {}) =>
    apiRequest(`/sdd/${companyId}/events${sddQuery(params)}`),

  createEvent: async (companyId, data) =>
    apiRequest(`/sdd/${companyId}/events`, { method: "POST", body: data }),

  updateEvent: async (companyId, id, data) =>
    apiRequest(`/sdd/${companyId}/events/${id}`, { method: "PATCH", body: data }),

  /** One-way. Fixes the instant the information stopped being UPSI. */
  publishEvent: async (companyId, id, data) =>
    apiRequest(`/sdd/${companyId}/events/${id}/publish`, { method: "POST", body: data }),

  // --- The register ---
  getRegister: async (companyId, params = {}) =>
    apiRequest(`/sdd/${companyId}/shares${sddQuery(params)}`),

  getRegisterRow: async (companyId, id) => apiRequest(`/sdd/${companyId}/shares/${id}`),

  recordShare: async (companyId, data) =>
    apiRequest(`/sdd/${companyId}/shares`, { method: "POST", body: data }),

  /** Supersede a row. The original stays in the register, struck through. */
  correctShare: async (companyId, id, data) =>
    apiRequest(`/sdd/${companyId}/shares/${id}/correction`, { method: "POST", body: data }),

  // --- Integrity ---
  verify: async (companyId) => apiRequest(`/sdd/${companyId}/verify`),

  /** `includePan` reveals full identifiers and is audited server-side. */
  exportRegister: async (companyId, { includePan = false } = {}) =>
    apiRequest(`/sdd/${companyId}/export${sddQuery({ includePan: includePan || null })}`),

  /**
   * Excel or PDF. Not apiRequest: that parses every response as JSON, which
   * would corrupt a binary body. Raw fetch, same auth header, returns a blob
   * plus the filename the server chose.
   */
  downloadRegister: async (companyId, { format = 'xlsx', includePan = false } = {}) => {
    const query = sddQuery({ format, includePan: includePan || null });
    const response = await fetch(`${API_BASE_URL}/sdd/${companyId}/export${query}`, {
      headers: getHeaders(true),
      credentials: 'include',
    });

    if (!response.ok) {
      // An error body IS json even when the success body would not be.
      let message = 'Export failed.';
      try {
        message = (await response.json()).message || message;
      } catch {
        /* non-JSON error body — keep the default */
      }
      throw new APIError(message, 'EXPORT_FAILED', response.status);
    }

    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    return {
      blob: await response.blob(),
      filename: match ? match[1] : `sdd-register.${format}`,
    };
  },

  // --- Pending entries suggested from activity in the platform ---
  getProposals: async (companyId, params = {}) =>
    apiRequest(`/sdd/${companyId}/proposals${sddQuery(params)}`),

  /**
   * Turn a suggestion into a sealed register entry. The event and the purpose
   * are required — they are the two things the platform cannot know, which is
   * why suggestions exist separately from the register at all.
   */
  confirmProposal: async (companyId, id, { upsiEvent, purpose, mode, sharedAt }) =>
    apiRequest(`/sdd/${companyId}/proposals/${id}/confirm`, {
      method: 'POST',
      body: { upsiEvent, purpose, mode, sharedAt },
    }),

  dismissProposal: async (companyId, id, reason) =>
    apiRequest(`/sdd/${companyId}/proposals/${id}/dismiss`, { method: 'POST', body: { reason } }),

  // --- Reg 3(2B) due notice to recipients ---
  getNotices: async (companyId, params = {}) =>
    apiRequest(`/sdd/${companyId}/notices${sddQuery(params)}`),

  /** Record an acknowledgement received outside the system. */
  acknowledgeNotice: async (companyId, id, note) =>
    apiRequest(`/sdd/${companyId}/notices/${id}/acknowledge`, { method: 'POST', body: { note } }),

  /**
   * The recipient's own acknowledgement, from the link in their email.
   * `includeAuth: false` — the external auditors and bankers who receive these
   * notices have no account here, which is the whole point of the token.
   */
  acknowledgeNotice_public: async (token) =>
    apiRequest(`/sdd/notice/${token}/acknowledge`, { method: 'POST', includeAuth: false }),

  resendNotice: async (companyId, id) =>
    apiRequest(`/sdd/${companyId}/notices/${id}/resend`, { method: 'POST' }),

  // --- Retention and legal hold (Reg 3(6)) ---
  getRetention: async (companyId) => apiRequest(`/sdd/${companyId}/retention`),

  getLegalHolds: async (companyId) => apiRequest(`/sdd/${companyId}/legal-holds`),

  /** Preserves the register past eight years while proceedings are open. */
  placeLegalHold: async (companyId, data) =>
    apiRequest(`/sdd/${companyId}/legal-holds`, { method: 'POST', body: data }),

  releaseLegalHold: async (companyId, id, releaseReason) =>
    apiRequest(`/sdd/${companyId}/legal-holds/${id}/release`, {
      method: 'POST',
      body: { releaseReason },
    }),
};

// ==================== SDD Monitoring (platform admin) ====================

/**
 * Platform-team monitoring. Counts, integrity status and health — never a
 * register row, a name, a PAN or a purpose. There is deliberately no
 * "view register" call here; if support needs client data, the company grants
 * it from their own screen.
 */
export const sddAdminAPI = {
  /** `integrity: false` skips the per-company chain walk. */
  getCompanies: async ({ integrity = true } = {}) =>
    apiRequest(`/admin/sdd/companies${integrity ? '' : '?integrity=false'}`),

  getCompany: async (companyId) => apiRequest(`/admin/sdd/companies/${companyId}`),

  // The UPSI mailbox — connected by the platform team at onboarding. A
  // credential, not register content; stored encrypted and never read back.
  getMailbox: async (companyId) => apiRequest(`/admin/sdd/companies/${companyId}/mailbox`),
  /** Password is required on first connection; blank on an update keeps the stored one. */
  setMailbox: async (companyId, settings) =>
    apiRequest(`/admin/sdd/companies/${companyId}/mailbox`, { method: 'PUT', body: settings }),
  testMailbox: async (companyId) => apiRequest(`/admin/sdd/companies/${companyId}/mailbox/test`, { method: 'POST' }),
  syncMailbox: async (companyId) => apiRequest(`/admin/sdd/companies/${companyId}/mailbox/sync`, { method: 'POST' }),
};

// ==================== Professional Assignment API ====================

export const professionalAssignmentAPI = {
  // Corporate user endpoints

  // Get all assignments for the current corporate user
  getMyAssignments: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `/professional-assignments/my-assignments${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Get revoked/inactive assignments
  getRevokedAccess: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `/professional-assignments/revoked-access${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Get available professionals to assign
  getAvailableProfessionals: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `/professional-assignments/available-professionals${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Get companies available for assignment
  getAvailableCompanies: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `/professional-assignments/available-companies${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Create a new professional assignment
  createAssignment: async (assignmentData) => {
    return apiRequest("/professional-assignments", {
      method: "POST",
      body: assignmentData,
    });
  },

  // Update an assignment
  updateAssignment: async (assignmentId, updateData) => {
    return apiRequest(`/professional-assignments/${assignmentId}`, {
      method: "PUT",
      body: updateData,
    });
  },

  // Delete/deactivate an assignment
  deleteAssignment: async (assignmentId) => {
    return apiRequest(`/professional-assignments/${assignmentId}`, {
      method: "DELETE",
    });
  },

  // Add company to existing assignment
  addCompanyToAssignment: async (assignmentId, companyId, accessLevel) => {
    return apiRequest(`/professional-assignments/${assignmentId}/companies`, {
      method: "POST",
      body: { companyId, accessLevel },
    });
  },

  // Remove company from assignment
  removeCompanyFromAssignment: async (assignmentId, companyId) => {
    return apiRequest(
      `/professional-assignments/${assignmentId}/companies/${companyId}`,
      { method: "DELETE" },
    );
  },

  // Get assignment details
  getAssignmentDetails: async (assignmentId) => {
    return apiRequest(`/professional-assignments/${assignmentId}`);
  },

  // What the assigned professional did on this corporate's companies
  // (company-scoped audit rows, cursor-paginated)
  getAssignmentActivity: async (assignmentId, { cursor, limit } = {}) => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit) params.set('limit', limit);
    const queryString = params.toString();
    return apiRequest(
      `/professional-assignments/${assignmentId}/activity${queryString ? `?${queryString}` : ''}`,
    );
  },

  // Professional user endpoints

  // Get pending assignments for current professional
  getMyPendingAssignments: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `/professional-assignments/professional/pending${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Get assignments for current professional
  getMyProfessionalAssignments: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `/professional-assignments/professional/my-assignments${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Get professional status history
  getProfessionalStatusHistory: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `/professional-assignments/professional/status-history${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Accept assignment
  acceptAssignment: async (assignmentId, notes = "") => {
    return apiRequest(
      `/professional-assignments/professional/${assignmentId}/accept`,
      {
        method: "POST",
        body: { notes },
      },
    );
  },

  // Reject assignment
  rejectAssignment: async (assignmentId, notes = "") => {
    return apiRequest(
      `/professional-assignments/professional/${assignmentId}/reject`,
      {
        method: "POST",
        body: { notes },
      },
    );
  },

  // Check professional access to company
  checkCompanyAccess: async (companyId, requiredAccess = "read") => {
    const queryString = new URLSearchParams({ requiredAccess }).toString();
    return apiRequest(
      `/professional-assignments/professional/check-access/${companyId}${queryString ? `?${queryString}` : ""}`,
    );
  },
};

// ==================== Health Check ====================

export const healthCheck = async () => {
  try {
    const response = await fetch(`${API_BASE_URL.replace("/api", "")}/health`);
    return response.json();
  } catch {
    return { success: false, status: "unavailable" };
  }
};

// ==================== Exports ====================

export { tokenManager, APIError };
export const isAuthenticated = () => tokenManager.isAuthenticated();
export const getCurrentUser = () => tokenManager.getUser();
export const logout = () => {
  tokenManager.clearAll();
  window.location.href = "/login";
};

// ==================== Notifications API ====================

export const notificationAPI = {
  // Get notifications — params: { view: 'inbox'|'unread'|'archived', type, page, limit }
  getNotifications: async (params = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ""),
    ).toString();
    return apiRequest(`/notifications${query ? `?${query}` : ""}`, {
      method: "GET",
    });
  },

  // Mark a notification as read
  markAsRead: async (notificationId) => {
    return apiRequest(`/notifications/${notificationId}/read`, {
      method: "PUT",
    });
  },

  // Mark all (inbox) notifications as read
  markAllAsRead: async () => {
    return apiRequest("/notifications/read-all", { method: "PUT" });
  },

  // Archive a single notification (moves it out of the inbox into history)
  archive: async (notificationId) => {
    return apiRequest(`/notifications/${notificationId}/archive`, {
      method: "PUT",
    });
  },

  // Restore an archived notification back to the inbox
  unarchive: async (notificationId) => {
    return apiRequest(`/notifications/${notificationId}/unarchive`, {
      method: "PUT",
    });
  },

  // Archive every read notification in the inbox at once
  archiveRead: async () => {
    return apiRequest("/notifications/archive-read", { method: "PUT" });
  },

  // Delete a notification permanently
  deleteNotification: async (notificationId) => {
    return apiRequest(`/notifications/${notificationId}`, { method: "DELETE" });
  },

  // Create a new notification (for system-generated notifications)
  createNotification: async (notificationData) => {
    return apiRequest("/notifications", {
      method: "POST",
      body: notificationData,
    });
  },

  // Get unread notification count
  getNotificationCount: async () => {
    return apiRequest("/notifications/count", { method: "GET" });
  },
};

// ==================== MCA form preparation ====================
// Staging for returns. Nothing here files anything with MCA - there is no
// submission API, and the return is signed with a DSC on the person's own
// machine. markFiled only RECORDS that they did it.
export const mcaFormsAPI = {
  forms: async () => apiRequest('/mca-forms'),
  drafts: async (companyId) => apiRequest(`/mca-forms/${companyId}`),
  open: async (companyId, formKey, financialYear) =>
    apiRequest(`/mca-forms/${companyId}/${formKey}`, { method: 'POST', body: { financialYear } }),
  get: async (companyId, formKey, fy) => apiRequest(`/mca-forms/${companyId}/${formKey}/${fy}`),
  save: async (companyId, formKey, fy, values) =>
    apiRequest(`/mca-forms/${companyId}/${formKey}/${fy}`, { method: 'PUT', body: { values } }),
  refresh: async (companyId, formKey, fy) =>
    apiRequest(`/mca-forms/${companyId}/${formKey}/${fy}/refresh`, { method: 'POST' }),
  markFiled: async (companyId, formKey, fy, srn, filedAt) =>
    apiRequest(`/mca-forms/${companyId}/${formKey}/${fy}/filed`, { method: 'POST', body: { srn, filedAt } }),
  exportDraft: async (companyId, formKey, fy) => apiRequest(`/mca-forms/${companyId}/${formKey}/${fy}/export`),
};

// ==================== Billing ====================
// Subscriptions, the trial clock and Razorpay checkout. The server is the
// authority on all of it - these screens only show what it says.
export const billingAPI = {
  me: async () => apiRequest('/billing/me'),
  plans: async () => apiRequest('/billing/plans'),
  checkout: async ({ planKey, cycle, extraCompanies = 0 }) =>
    apiRequest('/billing/checkout', { method: 'POST', body: { planKey, cycle, extraCompanies } }),
  // Tells the screen what happened. Access itself is granted by the webhook.
  verify: async (payload) => apiRequest('/billing/verify', { method: 'POST', body: payload }),
  cancel: async () => apiRequest('/billing/cancel', { method: 'POST' }),
};

// ==================== Help widget ====================
// The bubble in the corner. Answers come from the product help library on the
// server, not from an AI call - instant, free per question, and incapable of
// inventing a screen. A miss is a miss, and the widget offers a person.
export const helpAPI = {
  topics: async () => apiRequest('/help/topics'),
  ask: async (question) => apiRequest('/help/ask', { method: 'POST', body: { question } }),
  topic: async (id) => apiRequest(`/help/topic/${id}`),
};

// ==================== Ticket / Support API ====================

export const ticketAPI = {
  // User endpoints
  createTicket: async (data) => {
    return apiRequest('/tickets', { method: 'POST', body: data });
  },
  getMyTickets: async (params = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return apiRequest(`/tickets/my${query ? `?${query}` : ''}`, { method: 'GET' });
  },
  getTicket: async (ticketId) => {
    return apiRequest(`/tickets/${ticketId}`, { method: 'GET' });
  },
  addReply: async (ticketId, message) => {
    return apiRequest(`/tickets/${ticketId}/reply`, {
      method: 'POST',
      body: { message },
    });
  },

  // Admin endpoints
  getAllTickets: async (params = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    return apiRequest(`/tickets/admin/all${query ? `?${query}` : ''}`, { method: 'GET' });
  },
  updateTicket: async (ticketId, data) => {
    return apiRequest(`/tickets/${ticketId}`, { method: 'PATCH', body: data });
  },
  getTicketStats: async () => {
    return apiRequest('/tickets/admin/stats', { method: 'GET' });
  },
};

// ==================== Professional Invitation API ====================

export const professionalInvitationAPI = {
  // Quick create professional for corporate invitation flow
  quickCreateProfessional: async (data) => {
    return apiRequest('/professional-invitation/quick-create', {
      method: 'POST',
      body: data,
    });
  },

  // Search professionals (for assignment flow)
  searchProfessionals: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/professional-invitation/search${query ? `?${query}` : ''}`, { method: 'GET' });
  },

  // Verify invitation token (public endpoint)
  verifyInviteToken: async (data) => {
    const response = await fetch(`${API_BASE_URL}/professional-invitation/verify-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  // Complete onboarding (public endpoint)
  completeOnboarding: async (data) => {
    const response = await fetch(`${API_BASE_URL}/professional-invitation/complete-onboarding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  // Resend invitation (corporate user endpoint)
  resendInvitation: async (professionalId) => {
    return apiRequest(`/professional-invitation/${professionalId}/resend`, {
      method: 'POST',
    });
  },

  // Get invitation status (corporate user endpoint)
  getInvitationStatus: async (professionalId) => {
    return apiRequest(`/professional-invitation/${professionalId}/status`, { method: 'GET' });
  },
};

// Legacy exports for backward compatibility
export const setAuthToken = tokenManager.setToken;
export const getAuthToken = tokenManager.getToken;
export const removeAuthToken = tokenManager.removeToken;
export const setCurrentUser = tokenManager.setUser;
export const removeCurrentUser = tokenManager.removeUser;

// ==================== Statutory Registers API ====================
// The eleven registers under the Companies Act, 2013 (MGT-1, MGT-2, Register of
// Directors & KMP, SH-2/3/6/10, CHG-7, MBP-2/3/4).
//
// Every write returns the whole book rather than the row that changed. That is
// intentional: registers carry running balance columns the server recomputes
// over all rows on each save, so patching one row into local state would leave
// the balances beneath it stale and wrong on screen.

const registerPath = (companyId, registerType, seriesKey) => {
  const base = `/registers/${companyId}/${registerType}`;
  return seriesKey ? `${base}?seriesKey=${encodeURIComponent(seriesKey)}` : base;
};

// The class of shares travels as a query param, so a sub-path has to carry it
// through rather than append it after its own path segments.
const registerSubPath = (companyId, registerType, seriesKey, suffix) => {
  const base = `/registers/${companyId}/${registerType}${suffix}`;
  return seriesKey ? `${base}?seriesKey=${encodeURIComponent(seriesKey)}` : base;
};

/** Authenticated XLSX download, saved with the filename the server chose. */
const downloadWorkbook = async (path, fallbackName) => {
  const token = tokenManager.getToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new APIError(
      error.message || 'Register export failed',
      error.code || 'EXPORT_ERROR',
      response.status,
    );
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = match ? match[1] : fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return { success: true };
};

export const registersAPI = {
  /** The eleven prescribed forms — columns, citations, layout. No company data. */
  getDefinitions: async () => apiRequest('/registers/definitions'),

  /** The Title Sheet: every register and what this company holds in it. */
  list: async (companyId) => apiRequest(`/registers/${companyId}`),

  /** One book. Opens an empty one if the company has never written to it. */
  get: async (companyId, registerType, seriesKey = '') =>
    apiRequest(registerPath(companyId, registerType, seriesKey)),

  updateHeader: async (companyId, registerType, seriesKey, header) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, '/header'), {
      method: 'PUT',
      body: { header },
    }),

  updateFooter: async (companyId, registerType, seriesKey, footer) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, '/footer'), {
      method: 'PUT',
      body: { footer },
    }),

  /** Fill from MCA master data. Returns `sync` counts alongside the book. */
  sync: async (companyId, registerType, seriesKey = '') =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, '/sync'), { method: 'POST' }),

  /**
   * Fill every register that has a source from the company's own record, in one
   * pass. Returns `fill` — per-register counts plus how many books had the
   * company's name and registered office stamped on them.
   */
  fillFromCompany: async (companyId) =>
    apiRequest(`/registers/${companyId}/fill`, { method: 'POST' }),

  // --- Rows on a table register ---
  addEntry: async (companyId, registerType, seriesKey, { table = 'main', data, overrides }) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, '/entries'), {
      method: 'POST',
      body: { table, data, overrides },
    }),

  updateEntry: async (companyId, registerType, seriesKey, entryId, { data, overrides }) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, `/entries/${entryId}`), {
      method: 'PATCH',
      body: { data, overrides },
    }),

  deleteEntry: async (companyId, registerType, seriesKey, entryId) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, `/entries/${entryId}`), {
      method: 'DELETE',
    }),

  reorderEntries: async (companyId, registerType, seriesKey, table, order) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, '/entries/reorder'), {
      method: 'PUT',
      body: { table, order },
    }),

  // --- Folios, on MGT-1 / MGT-2 / Directors & KMP ---
  addFolio: async (companyId, registerType, seriesKey, { folioNo, details }) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, '/folios'), {
      method: 'POST',
      body: { folioNo, details },
    }),

  updateFolio: async (companyId, registerType, seriesKey, folioId, { folioNo, details }) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, `/folios/${folioId}`), {
      method: 'PATCH',
      body: { folioNo, details },
    }),

  deleteFolio: async (companyId, registerType, seriesKey, folioId) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, `/folios/${folioId}`), {
      method: 'DELETE',
    }),

  addFolioEntry: async (companyId, registerType, seriesKey, folioId, { data, overrides }) =>
    apiRequest(registerSubPath(companyId, registerType, seriesKey, `/folios/${folioId}/entries`), {
      method: 'POST',
      body: { data, overrides },
    }),

  updateFolioEntry: async (companyId, registerType, seriesKey, folioId, entryId, { data, overrides }) =>
    apiRequest(
      registerSubPath(companyId, registerType, seriesKey, `/folios/${folioId}/entries/${entryId}`),
      { method: 'PATCH', body: { data, overrides } },
    ),

  deleteFolioEntry: async (companyId, registerType, seriesKey, folioId, entryId) =>
    apiRequest(
      registerSubPath(companyId, registerType, seriesKey, `/folios/${folioId}/entries/${entryId}`),
      { method: 'DELETE' },
    ),

  reorderFolioEntries: async (companyId, registerType, seriesKey, folioId, order) =>
    apiRequest(
      registerSubPath(companyId, registerType, seriesKey, `/folios/${folioId}/entries/reorder`),
      { method: 'PUT', body: { order } },
    ),

  // --- Export, in the prescribed format ---
  exportRegister: async (companyId, registerType, seriesKey = '') =>
    downloadWorkbook(
      registerSubPath(companyId, registerType, seriesKey, '/export'),
      `${registerType}.xlsx`,
    ),

  /** All eleven registers plus a Title Sheet index, as one workbook. */
  exportBook: async (companyId) =>
    downloadWorkbook(`/registers/${companyId}/book/export`, 'statutory-registers.xlsx'),
};
